import { applyCameraInterfacesMutation, getCameraInterfacesOptions } from '@/api/systemAnalyzerApi'
import { Client } from '@/api/systemAnalyzerApi/client'
import { ProposedCameraInterface } from '@/api/systemAnalyzerApi/types.gen'
import CopyableValue from '@/components/Common/CopyableValue'
import ErrorState from '@/components/Common/ErrorState'
import { extendKey } from '@/utils/queryKey'
import { Alert, Badge, Button, Card, Group, List, LoadingOverlay, Stack, Table, Tabs, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FaSync } from 'react-icons/fa'
import { TbAlertTriangle, TbDeviceCctv } from 'react-icons/tb'
import { useDiagnosticsQuery } from './useDiagnosticsQuery'
import { isCameraInterfaceApplyResult, isCameraInterfaceProposal } from './validation'

const INTERFACE_STATUS_COLORS: Record<ProposedCameraInterface['status'], string> = {
  ok: 'green',
  rename: 'blue',
  conflict: 'red',
  unresolved: 'yellow',
}

const CameraInterfacesPanel = ({ client, source }: { client: Client; source: string }) => {
  const { t } = useTranslation(['diagnostics', 'common'])
  const queryClient = useQueryClient()

  const proposalQueryKey = extendKey(getCameraInterfacesOptions({ client }), source).queryKey

  const { data, isLoading, isFetching, isError, refetch } = useDiagnosticsQuery(
    extendKey(getCameraInterfacesOptions({ client }), source)
  )

  const proposal = isCameraInterfaceProposal(data) ? data : undefined
  const hasInvalidResponse = !isError && data !== undefined && !proposal

  const { mutate: applyInterfaces, isPending: isApplying } = useMutation({
    ...applyCameraInterfacesMutation({ client }),
    onSuccess: result => {
      if (!isCameraInterfaceApplyResult(result)) {
        notifications.show({ title: t('applyInterfacesFailed'), message: t('unexpectedResponse'), color: 'red' })
      } else if (result.apply_error) {
        notifications.show({ title: t('applyInterfacesFailed'), message: result.apply_error, color: 'red' })
      } else {
        notifications.show({
          title: t('applyInterfacesSucceeded'),
          message: t('applyInterfacesSucceededMessage', { count: result.applied?.length ?? 0 }),
          color: 'green',
        })
      }
      queryClient.invalidateQueries({ queryKey: proposalQueryKey })
    },
    onError: () => notifications.show({ title: t('applyInterfacesFailed'), message: '', color: 'red' }),
  })

  const confirmApply = () =>
    modals.openConfirmModal({
      title: t('applyInterfaces'),
      children: (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {t('confirmApplyInterfaces', { source: source.toUpperCase() })}
          </Text>
          <List size="sm" spacing="xs">
            <List.Item>{t('confirmApplyInterfacesBreakage')}</List.Item>
            <List.Item>{t('confirmApplyInterfacesHardware')}</List.Item>
          </List>
        </Stack>
      ),
      labels: { confirm: t('applyInterfaces'), cancel: t('cancel', { ns: 'common' }) },
      confirmProps: { color: 'red' },
      onConfirm: () => applyInterfaces({}),
    })

  return (
    <Tabs.Panel value="camera-interfaces">
      <Card shadow="sm" p="lg" radius="md" withBorder mt="lg" pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Group justify="space-between" align="center" mb="md">
          <Group align="center">
            <Text fw={500}>{t('cameraInterfaces')}</Text>
            {proposal?.source && (
              <Badge variant="light" color="gray">
                {t('cameraSource', { source: proposal.source })}
              </Badge>
            )}
          </Group>
          <Group gap="xs">
            <Button
              color="red"
              size="xs"
              leftSection={<TbDeviceCctv size={14} />}
              onClick={confirmApply}
              loading={isApplying}
              disabled={!proposal}
            >
              {t('applyInterfaces')}
            </Button>
            <Button
              variant="light"
              size="xs"
              leftSection={<FaSync size={12} />}
              onClick={() => refetch()}
              loading={isFetching}
            >
              {t('refresh')}
            </Button>
          </Group>
        </Group>

        {isError && (
          <ErrorState
            title={t('cameraInterfacesFetchFailed')}
            message={t('cameraInterfacesFetchFailedMessage', { source: source.toUpperCase() })}
            showBackButton={false}
            width="100%"
          />
        )}

        {hasInvalidResponse && (
          <ErrorState
            title={t('unexpectedResponse')}
            message={t('cameraInterfacesUnexpectedResponseMessage', { source: source.toUpperCase() })}
            showBackButton={false}
            width="100%"
          />
        )}

        {proposal && (
          <Stack gap="lg">
            {proposal.error && (
              <Alert icon={<TbAlertTriangle size={16} />} color="red" variant="light">
                {proposal.error}
              </Alert>
            )}

            <Stack gap="xs">
              <Text fw={500}>{t('proposedInterfaces')}</Text>
              {proposal.proposed.length === 0 ? (
                <Text size="sm">{t('noProposal')}</Text>
              ) : (
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('cameraName')}</Table.Th>
                      <Table.Th>{t('cameraIp')}</Table.Th>
                      <Table.Th>{t('proposedName')}</Table.Th>
                      <Table.Th>{t('currentInterface')}</Table.Th>
                      <Table.Th>{t('mac')}</Table.Th>
                      <Table.Th>{t('status')}</Table.Th>
                      <Table.Th>{t('detail')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {proposal.proposed.map(entry => (
                      <Table.Tr key={`${entry.serial}-${entry.camera_ip}`}>
                        <Table.Td>{entry.user_defined_name}</Table.Td>
                        <Table.Td>
                          <CopyableValue value={entry.camera_ip} />
                        </Table.Td>
                        <Table.Td>
                          <CopyableValue value={entry.name} />
                        </Table.Td>
                        <Table.Td>
                          <CopyableValue value={entry.iface} />
                        </Table.Td>
                        <Table.Td>
                          <CopyableValue value={entry.mac} />
                        </Table.Td>
                        <Table.Td>
                          <Badge color={INTERFACE_STATUS_COLORS[entry.status]} variant="light">
                            {t(`interfaceStatus_${entry.status}`)}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{entry.detail}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>

            <Stack gap="xs">
              <Text fw={500}>{t('conflicts')}</Text>
              {proposal.conflicts.length === 0 ? (
                <Text size="sm">{t('noConflicts')}</Text>
              ) : (
                proposal.conflicts.map(conflict => (
                  <Alert
                    key={`${conflict.reason}-${conflict.name ?? conflict.iface}`}
                    icon={<TbAlertTriangle size={16} />}
                    color="red"
                    variant="light"
                    title={t(`conflictReason_${conflict.reason}`)}
                  >
                    <Text size="sm">{conflict.name ?? conflict.iface}</Text>
                    <Text size="sm">{conflict.entries.map(entry => entry.detail).join(', ')}</Text>
                  </Alert>
                ))
              )}
            </Stack>

            <Stack gap="xs">
              <Text fw={500}>{t('detectedCameras')}</Text>
              {proposal.cameras.length === 0 ? (
                <Text size="sm">{t('noCameras')}</Text>
              ) : (
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('cameraName')}</Table.Th>
                      <Table.Th>{t('cameraIp')}</Table.Th>
                      <Table.Th>{t('model')}</Table.Th>
                      <Table.Th>{t('serial')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {proposal.cameras.map((camera, index) => (
                      <Table.Tr key={camera.serial ?? camera.ip_address ?? index}>
                        <Table.Td>{camera.user_defined_name ?? t('notAvailable', { ns: 'common' })}</Table.Td>
                        <Table.Td>
                          <CopyableValue value={camera.ip_address} />
                        </Table.Td>
                        <Table.Td>
                          <CopyableValue value={camera.model} />
                        </Table.Td>
                        <Table.Td>
                          <CopyableValue value={camera.serial} />
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Stack>
        )}
      </Card>
    </Tabs.Panel>
  )
}

export default CameraInterfacesPanel
